import { db } from './firebase-init';
import { collection, getDocs, query, where } from 'firebase/firestore';

/**
 * Get all users who can own estimates — salespersons + office manager + admin.
 * Sorted alphabetically by displayName.
 *
 * Used by:
 *   - Assignee picker on QuickQuotePage (office manager + admin only)
 *   - Reassign menu in three-dot dropdown
 */
export async function getAssignableUsers() {
  // We want sales, officeManager, admin, manager — anyone who legitimately can own an estimate.
  // Firestore 'in' query supports up to 30 values, plenty for our use case.
  const ASSIGNABLE_ROLES = ['sales', 'officeManager', 'admin', 'manager'];

  const q = query(
    collection(db, 'users'),
    where('role', 'in', ASSIGNABLE_ROLES),
  );
  const snap = await getDocs(q);

  const users = snap.docs.map(d => {
    const data = d.data();
    return {
      uid:           d.id,
      displayName:   data.displayName || data.name || data.email || 'Unknown',
      email:         data.email || '',
      role:          data.role || '',
      displayPhone:  data.displayPhone || '605-432-1834',
      notificationPhone: data.notificationPhone || null,
    };
  });

  // Sort: salespersons first (sorted by name), then officeManager/admin
  return users.sort((a, b) => {
    const aSales = a.role === 'sales' ? 0 : 1;
    const bSales = b.role === 'sales' ? 0 : 1;
    if (aSales !== bSales) return aSales - bSales;
    return a.displayName.localeCompare(b.displayName);
  });
}

/**
 * Get a single user by UID. Returns null if not found.
 * Used for resolving estimate owner data when generating PDFs etc.
 */
export async function getUserByUid(uid) {
  if (!uid) return null;
  try {
    const q = query(
      collection(db, 'users'),
      where('__name__', '==', uid),
    );
    const snap = await getDocs(q);
    if (snap.empty) return null;
    const data = snap.docs[0].data();
    return {
      uid,
      displayName:       data.displayName || data.name || data.email || 'Unknown',
      email:             data.email || '',
      role:              data.role || '',
      displayPhone:      data.displayPhone || '605-432-1834',
      notificationPhone: data.notificationPhone || null,
    };
  } catch (e) {
    console.error('getUserByUid error:', e);
    return null;
  }
}
